import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchJson } from "@/utils/api";
import { useGameStore } from "@/store/gameStore";
import type { JoinPreviewResponse, JoinGameResponse } from "@/types/api";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";
import Badge from "@/components/ui/Badge";
import { Search, UserPlus, ArrowLeft, User } from "lucide-react";

export default function JoinGamePage() {
  const { slug: urlSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const addSession = useGameStore((s) => s.addSession);

  const [slug, setSlug] = useState(() => {
    if (!urlSlug) return "";
    const raw = urlSlug.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4, 8)}` : raw;
  });
  const [preview, setPreview] = useState<JoinPreviewResponse | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-preview if slug from URL
  useEffect(() => {
    if (urlSlug) {
      loadPreview(urlSlug);
    }
  }, [urlSlug]);

  async function loadPreview(s: string) {
    const normalized = s.replace(/-/g, "").toUpperCase();
    if (normalized.length < 8) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const data = await fetchJson<JoinPreviewResponse>(
        `/join/${normalized}`,
      );
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(err instanceof Error ? err.message : "Game not found");
    } finally {
      setLoadingPreview(false);
    }
  }

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8);
    if (raw.length > 4) {
      setSlug(`${raw.slice(0, 4)}-${raw.slice(4)}`);
    } else {
      setSlug(raw);
    }
  };

  const handleSlugSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadPreview(slug);
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    setJoining(true);
    setError(null);
    try {
      const normalized = slug.replace(/-/g, "").toUpperCase();
      const res = await fetchJson<JoinGameResponse>("/join", {
        method: "POST",
        body: { slug: normalized, displayName: displayName.trim() },
      });

      // Format slug for display
      const formatted =
        normalized.slice(0, 4) + "-" + normalized.slice(4, 8);

      addSession({
        gameId: res.gameId,
        slug: formatted,
        gameTitle: res.gameTitle,
        playerToken: res.token,
        playerId: res.playerId,
        displayName: res.displayName,
        role: "player",
        isModerator: false,
        joinedAt: Date.now(),
      });

      navigate(`/game/${res.gameId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to join game");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-text">
        Join a Game
      </h1>

      {!preview ? (
        <form onSubmit={handleSlugSubmit} className="mt-6 space-y-4">
          <Input
            label="Invite Code"
            value={slug}
            onChange={handleSlugChange}
            placeholder="ABCD-EFGH"
            maxLength={9}
            autoCapitalize="characters"
            style={{ fontFamily: "monospace", fontSize: "1.25rem", letterSpacing: "0.15em" }}
          />
          {error && <p className="text-base text-error">{error}</p>}
          <Button
            type="submit"
            variant="primary"
            loading={loadingPreview}
            disabled={slug.replace(/-/g, "").length < 8}
            className="w-full"
          >
            <Search className="h-4 w-4" />
            Look Up Game
          </Button>
        </form>
      ) : (
        <div className="mt-6">
          <div className="rounded-2xl border border-border bg-surface-raised p-4">
            {loadingPreview ? (
              <div className="flex justify-center py-4">
                <Spinner className="h-5 w-5" />
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl font-bold text-text">
                  {preview.title}
                </h2>
                <p className="mt-1 inline-flex items-center gap-1 text-base text-text-secondary">
                  <User className="h-3.5 w-3.5" />
                  Hosted by {preview.ownerDisplayName}
                </p>
                <div className="mt-2 flex gap-2">
                  <Badge variant="primary">{preview.status}</Badge>
                  <Badge variant="muted">
                    {preview.playerCount} player
                    {preview.playerCount !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </>
            )}
          </div>

          <form onSubmit={handleJoin} className="mt-4 space-y-4">
            <Input
              label="Your Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alex"
              maxLength={30}
              required
            />
            {error && <p className="text-base text-error">{error}</p>}
            <Button
              type="submit"
              variant="primary"
              loading={joining}
              disabled={!displayName.trim()}
              className="w-full"
            >
              <UserPlus className="h-4 w-4" />
              Join Game
            </Button>
          </form>

          <button
            onClick={() => {
              setPreview(null);
              setError(null);
            }}
            className="mt-3 inline-flex items-center gap-1.5 text-base text-text-secondary hover:text-text"
          >
            <ArrowLeft className="h-4 w-4" />
            Use a different code
          </button>
        </div>
      )}
    </div>
  );
}
